export const EVACUATION_LEVEL_TYPES = {
  SECURITY: "Security",
  POLITICS: "Politics",
  NATURAL_DISASTERS: "Natural disasters",
  COMM_AND_SUPPLIES: "Communication and supplies",
  TRAVEL: "Travel",
  POLITICAL: "Political",
};

export const EVACUATION_METHODOLOGY_DESCRIPTION = {
  name: "METHODOLOGY",
  details:
    "<p>Evacuation Watch provides guidance on the appropriate level of evacuation planning advisable in a country based on Seerist and Control Risks&rsquo; assessment of the on-the-ground situation. Levels are set for whole countries. Evacuation Watch takes into account:</p>" +
    "<p><ul>" +
    "<li>political and security risks to personnel</li>" +
    "<li>threats from natural disasters</li>" +
    "<li>availability of communications, essential services and basic supplies</li>" +
    "<li>availability of secure transport within and out of a country</li>" +
    "</ul></p>" +
    "<p>These factors will vary depending on specific location within a country, and on the profile and nationality of anorganisation and its personnel. Evacuation Watch is intended as guidance for security or country managers. It is not a substitute for detailed evacuation planning specific to an individual organisation&rsquo;s footprint, workforce and exposure: Control Risks has extensive experience in this field and can assist if required.</p>",
};

export const EVACUATION_LEVEL_DESCRIPTIONS = [
  {
    name: "EMERGENCY EVACUATION",
    description:
      "Risks to personnel are – or will very imminently become – extreme and largely unmanageable, and secure transport options are very limited: all staff should be evacuated.",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "war or civil war affects large parts of the country; law and order have collapsed, with high levels of crime and instability making it unsafe to go out; mob violence, criminals, kidnap groups and/or terrorists target foreigners.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICS,
        value: 2,
        details:
          "the government has lost control and power is violently contested by armed groups or widescale popular mobilisation. Alternatively, the administration – or an incoming regime – is actively hostile to some or all foreigners, who are at risk of unlawful detention or being prevented from leaving.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "there is severe damage to infrastructure from a natural disaster. The availability of safe shelter is very limited; temporary shelters are in use.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.COMM_AND_SUPPLIES,
        value: 5,
        details:
          "essential supplies cannot be easily or safely acquired; utilities (power and water) are severely disrupted; satellite communications are essential.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "lengthy curfews significantly curtail movement. Travel to airports, ports or borders is unsafe due to serious security risks or the poor state of infrastructure. Commercial airlines are no longer operating or are planning to imminently end services. Land exits are severely hindered by security risks; infrastructure weaknesses; and/or insecurity or strict entry requirements in neighbouring states.",
      },
    ],
    advices: [
      "Activate emergency evacuation procedures as appropriate to the on-the-ground situation",
      "Cease business activity; secure or destroy sensitive information; secure assets as best as possible",
      "Liaise with embassies and other cooperating companies",
    ],
  },
  {
    name: "NON-ESSENTIAL EVACUATION",
    description:
      "Security and operational conditions pose a serious threat to staff and their families that cannot be fully managed, and the situation is likely to persist or deteriorate. Movement is restricted, there are few secure options for leaving the country and these could be blocked at short notice. Non-essential staff and families should be evacuated. For example:",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "the government is under clear threat from a rapidly spreading insurgency; insecurity from riots, crime,kidnap and/or terrorism poses a significant threat, in particular to foreigners; there is an imminent risk of cross-border invasion.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICS,
        value: 2,
        details:
          "the government is losing control and being challenged by popular unrest, armed groups or separatist forces; there are strong indicators that a coup is imminent; alternatively, the administration may be hostile to some or all foreigners.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "a disaster has triggered shortages and interruptions to utilities, supplies, communications and transport (see below). There is widespread damage to shelter in residential areas.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.COMM_AND_SUPPLIES,
        value: 5,
        details:
          "shortages of essential supplies occur regularly; power and water are regularly disrupted; mobile, internet and landline communications are unreliable due to infrastructure damage or government shutdowns.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "curfews restrict movement at certain times. Travel to airports, ports or borders is hindered by insecurity or poor infrastructure; few options are available. Commercial airlines continue to operate but services are disrupted and or difficult to access. Land routes out are accessible but subject to security risks, weak infrastructure and/or strict entry requirements or insecurity in neighbouring states.",
      },
    ],
    advices: [
      "Withdraw all personnel except stay-behind group of key staff",
      "Identify two evacuation routes; secure assembly areas, routes and departure points; strictly control movement;and ensure personnel are placed on 12-hour notice to move",
      "Maintain food and water supplies in safe havens; ensure vehicles are fuelled",
      "Secure sites, including residences, and secure or destroy sensitive information",
    ],
  },
  {
    name: "STANDBY",
    description:
      "Staff face risks from insecurity and on-the-ground conditions with a significant likelihood of escalation or spreading countrywide at short notice. Secure exit options are limited and subject to short-notice restrictions. Evacuation plans should be ready for immediate activation. For example:",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "crime, insurgency and/or terrorist activity pose increasing risks to staff; insurgency or civil war is expanding into new areas; disputes with a neighbour are escalating through threatening rhetoric and military activity; security forces are unable to fully perform their duties and may themselves pose a threat.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICS,
        value: 2,
        details:
          "there is a serious threat of political instability, including the possibility of a coup. The administration is challenged by popular protest (some of which may be violent or involve clashes with security forces) or insurgency.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "a potentially high-impact natural disaster is imminent (hurricane, cyclone, etc.), with potential to cause widespread operational disruption. Impacts are expected to be resolved within days, but there is a risk of more severe damage or disruption (this may be more limited to some areas).",
      },
      {
        name: EVACUATION_LEVEL_TYPES.COMM_AND_SUPPLIES,
        value: 5,
        details:
          "some essential supplies are affected by shortages and prices are rising; power and water are occasionally disrupted; mobile, internet and landline are available, but service may be patchy and the government may shut down communications for security reasons.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "temporary or short-term curfews may be imposed. Travel to airports, ports and borders is possible but some routes may close at short notice, be subject to security risks or complicated by entry requirements. International flights face limited disruption but places remain available.",
      },
    ],
    advices: [
      "Rehearse evacuation plans, place expatriates on 24 hours’ notice to move; and restrict movement",
      "Prepare vehicles for evacuation role, with fuel reserve maintained",
      "Maintain two-week food and water supplies in safe havens",
      "Prepare for handover to stay-behind group",
    ],
  },
  {
    name: "CAUTION",
    description:
      "Security and operational threats are manageable but the potential for rapid deterioration means organisations should have full evacuation plans in readiness for immediate implementation. For example:",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "security threats may be more acute in some areas of the country, with a risk of limited spread beyond these areas; the country borders an active warzone but with low likelihood of spillover; the country has significant territorial disputes with a neighbour that may see occasional low-level skirmishes or exchanges of fire.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICS,
        value: 2,
        details:
          "political instability can have security implications; institutions are weak; there is a recent history of disrupted, unscheduled or violent transitions; political, ethnic, religious or social divisions could lead to a rapid deterioration in security in the event of a trigger event.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "the country is vulnerable to wide-scale natural disaster, including events that could occur with little warning (such as earthquakes, volcanos), and lacks the resilience to cope with such events.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.COMM_AND_SUPPLIES,
        value: 5,
        details:
          "basic supplies are available, and power and water is largely reliable.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "transport remains open with no restrictions on movement and several options for exit routes.",
      },
    ],
    advices: [
      "Ensure evacuation plans are in place, regularly reviewed and kept to up to date",
      "Ensure expatriates are registered with relevant embassies",
      "Ensure all personnel have up to date documentation (passports, visas, vaccinations) along with access to currency (local, US and foreign currencies on evacuation routes)",
      "Establish contact with embassies and diplomatic information channels",
    ],
  },
  {
    name: "PREPARATION",
    description:
      "Insecurity may pose occasional threats to staff, but these are manageable and sudden deterioration is unlikely.However, organisations are advised to prepare evacuation plans because there is a risk of deterioration in security conditions, vulnerability to natural hazards and/or potential difficulties in extracting people from the country. For example:",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "part of the country is affected by insurgency or terrorism, but the threat is contained and unlikely to spread in the near future.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICAL,
        value: 3,
        details:
          "the country may have a history of political, ethnic, religious or social divisions, but the likelihood of these triggering a sudden and sustained increase in security threats is low. Political institutions may be fragile;there may be a history of unscheduled, disrupted or violent political transitions but there no indications that such an event is imminent",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "the country is vulnerable to a wide-scale natural disaster, but such events are very rare and/or occur with warning (e.g. cyclone, hurricane). The country is generally well prepared for natural disasters but would be overwhelmed by a particularly severe event.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "there are no current restrictions on movement but there are few exit routes, reducing evacuation options in the event of a crisis.",
      },
    ],
    advices: [
      "Draft evacuation plans that align with evacuation policy",
      "Ensure in-country personnel have valid passports",
    ],
  },
  {
    name: "NO PLAN REQUIRED",
    description:
      "The likelihood of insecurity or natural disasters requiring evacuation is very low: full evacuation plans are not required.",
    types: [
      {
        name: EVACUATION_LEVEL_TYPES.SECURITY,
        value: 1,
        details:
          "there is a minimal risk of the country being directly affected by external conflict.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.POLITICS,
        value: 2,
        details:
          "the country is politically stable with strong institutions. Ethnic, social or other divisions may exist but are unlikely to trigger widespread security incidents that would pose a threat to staff.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.NATURAL_DISASTERS,
        value: 4,
        details:
          "the country is not vulnerable to nationwide natural disasters and has the resilience to respond effectively to even severe events, though there may be some short-term operational impact within areas directly affected.",
      },
      {
        name: EVACUATION_LEVEL_TYPES.TRAVEL,
        value: 6,
        details:
          "international transport infrastructure is reliable, with multiple secure options to exit the country.",
      },
    ],
    advices: ["Develop and maintain company evacuation policy"],
  },
];
